// Paddle.js, Paddle's checkout script: loaded from Paddle's CDN (Paddle allows no other copy), and only on /checkout/pay,
// whose Content-Security-Policy alone allows it (serverless.yml's PaymentPageResponseHeadersPolicy). Card details go into
// the checkout's frame, on Paddle's domain: never into this site.
const SCRIPT_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';
const LOAD_TIMEOUT_MS = 20000;

// What the pay page uses of it.
export interface PaddleEvent {
    name?: string;
}

export interface PaddleCheckoutOptions {
    transactionId: string;
    settings?: {
        displayMode?: 'overlay' | 'inline';
        successUrl?: string;
        theme?: 'light' | 'dark';
        allowLogout?: boolean;
    };
}

export interface PaddleJs {
    Environment: { set(environment: 'sandbox'): void };
    Initialize(options: { token: string, eventCallback?: (event: PaddleEvent) => void }): void;
    Checkout: { open(options: PaddleCheckoutOptions): void };
}

let loading: Promise<PaddleJs> | undefined;

// Leaves the pay page as a full page load, so Paddle.js (and the page's looser policy) don't stay with the customer.
export function leavePayPage(url: string): void {
    window.location.assign(url);
}

export function loadPaddle(): Promise<PaddleJs> {
    loading ??= new Promise<PaddleJs>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = SCRIPT_URL;
        script.async = true;
        const timer = setTimeout(() => fail(new Error('Paddle.js took too long to load')), LOAD_TIMEOUT_MS);
        const fail = (err: Error) => {
            clearTimeout(timer);
            loading = undefined;
            script.remove();
            reject(err);
        };
        script.onload = () => {
            clearTimeout(timer);
            const paddle = (window as unknown as { Paddle?: PaddleJs }).Paddle;
            if (paddle) {
                resolve(paddle);
            } else {
                fail(new Error('Paddle.js loaded without defining Paddle'));
            }
        };
        script.onerror = () => fail(new Error('Paddle.js failed to load'));
        document.head.appendChild(script);
    });
    return loading;
}
